import { HttpClientModule, HttpEvent, HttpHandlerFn, HttpRequest, provideHttpClient, withInterceptors } from '@angular/common/http';
import { APP_INITIALIZER, ApplicationConfig, importProvidersFrom, inject, InjectionToken } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { AUTHENTICATION_CONFIG, AuthenticationService } from '@aodocs/authentication';
import { ConfigurationService } from '@aodocs/configuration-service';
import { LOGIN_PAGE_CONFIG } from '@aodocs/login-page';
import { AttachmentPreviewConfig } from '@aodocs/models';
import { SharedTranslationModule } from '@aodocs/shared-translation';
import { defaultInterceptor } from '@aodocs/utils-interceptors';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { Observable, of } from 'rxjs';

import { AppRoutingModule } from './app-routing.module';
import { FactoriesUtils } from './core/utils/factories';
import { authInterceptor } from './shared/interceptor/auth.interceptor';
import { addPreviewDomainAndLocaleInterceptor } from './shared/interceptor/domain-locale-interceptors';

export const CONFIG_URL = new InjectionToken<string>('ConfigUrl');

export const appConfig: ApplicationConfig = {
  providers: [
    ConfigurationService,
    {
      provide: CONFIG_URL,
      useValue: '/config/environment.json',
    },
    {
      provide: APP_INITIALIZER,
      useFactory: FactoriesUtils.initializeConfiguration,
      deps: [CONFIG_URL, ConfigurationService<AttachmentPreviewConfig>],
      multi: true,
    },
    {
      provide: AUTHENTICATION_CONFIG,
      useFactory: FactoriesUtils.initializeAuthenticationConfiguration,
      deps: [ConfigurationService<AttachmentPreviewConfig>],
    },
    {
      provide: LOGIN_PAGE_CONFIG,
      useFactory: FactoriesUtils.initializeLoginPageConfiguration,
    },
    { provide: Window, useValue: window },
    AuthenticationService,
    provideStore({ router: routerReducer }),
    provideRouterStore(),
    importProvidersFrom(BrowserModule, BrowserAnimationsModule, HttpClientModule, AppRoutingModule, SharedTranslationModule),
    provideHttpClient(
      withInterceptors([
        (httpRequest: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
          const configService = inject(ConfigurationService<AttachmentPreviewConfig>);
          const config: AttachmentPreviewConfig = configService.configuration;
          const service = inject(AuthenticationService);
          const interceptor = defaultInterceptor([config.storageApiUrl], () => of(service.getLimitedAccessToken()));
          return interceptor(httpRequest, next);
        },
        (httpRequest: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
          const configService = inject(ConfigurationService<AttachmentPreviewConfig>);
          const config: AttachmentPreviewConfig = configService.configuration;
          const service = inject(AuthenticationService);
          const interceptor = defaultInterceptor([config.storageApiUrl], () => service.getIdToken());
          return interceptor(httpRequest, next);
        },
        (httpRequest: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
          const configService = inject(ConfigurationService<AttachmentPreviewConfig>);
          const config: AttachmentPreviewConfig = configService.configuration;
          const service = inject(AuthenticationService);
          const interceptor = defaultInterceptor([config.aodocsApiUrl], () => service.getIdToken());
          return interceptor(httpRequest, next);
        },
        addPreviewDomainAndLocaleInterceptor,
        (httpRequest: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
          const service = inject(AuthenticationService);
          const router = inject(Router);
          const interceptor = authInterceptor(() => {
            service.signOut();
            const queryParams = router.routerState.root.snapshot.queryParams;
            router.navigate(['login'], { queryParams });
          });
          return interceptor(httpRequest, next);
        },
      ])
    ),
  ],
};
